import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button, InputGroup } from "react-bootstrap";
import {
    useBlocker,
    useLocation,
    useNavigate,
    useNavigationType,
    useParams
} from "react-router-dom";
import { BackButton } from "../../simple/BackButton";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faDatabase,
    faUpRightAndDownLeftFromCenter
} from "@fortawesome/free-solid-svg-icons";
import {
    AditorCreateInput,
    AditorCreateVariables,
    AditorInitQueryData,
    AditorLoadSceneData,
    AditorMessage,
    AditorSceneData,
    AditorSceneVariables,
    AditorUpdateData,
    AditorUpdateInput,
    AditorUpdateVariables,
    CreateAditorSceneData,
    CreateAditorSceneResult,
    DeleteAditorSceneData,
    UpdateAditorSceneData
} from "../../../models/aditor";
import {
    useApolloClient,
    useLazyQuery,
    useMutation,
    useQuery
} from "@apollo/client";
import {
    ADITOR_CREATE_SCENE,
    ADITOR_DELETE_SCENE,
    ADITOR_UPDATE_SCENE
} from "../../../graphql/mutations";
import {
    ADITOR_GET_SCENES,
    ADITOR_INIT_QUERY,
    ADITOR_LOAD_SCENE,
    GET_REVISION
} from "../../../graphql/queries";
import { AditorOptimizingModal } from "../../modals/aditor/AditorOptimizingModal";
import { useRevisionStandby } from "../../../graphql/hooks";
import { getAditorUrl, getSceneAsBase64 } from "../../../common/AditorHelpers";
import { AlertModal } from "../../modals/AlertModal";
import { RevisionData, RevisionVariables } from "../../../models/types";
import { AditorSnapshotModal } from "../../modals/aditor/AditorSnapshotModal";
import { LatestSceneBadge } from "../../simple/aditor/LatestSceneBadge";
import styles from "../../panels/EditPanel.module.scss";
import cx from "classnames";

const AditorViewImplementation = () => {
    const { revisionId } = useParams<{
        revisionId: string;
    }>();
    const client = useApolloClient();
    const revision = useRevisionStandby(Number(revisionId));
    const navigate = useNavigate();
    const location = useLocation();
    const navigationType = useNavigationType();
    const [aditorReady, updateAditorReady] = useState(false);
    const [htmlContent, setHtmlContent] = useState<string>("");
    const [showOptimizingModal, updateShowOptimizingModal] = useState(false);
    const [fullScreen, updateFullScreen] = useState(false);
    const [isBlocking, setIsBlocking] = useState(false);
    const [initData, updateInitData] = useState<
        AditorInitQueryData | undefined
    >(undefined);
    const [aditorKey, updateAditorKey] = useState(Date.now());
    const [currentSceneTag, updateCurrentSceneTag] = useState("");
    const [showSnapshots, updateShowSnapshots] = useState<{
        show: boolean;
        focus: string | undefined;
    }>({ show: false, focus: undefined });
    const lastLoadedSceneId = useRef<number | undefined>(undefined);
    const currentScene = useRef<string | undefined>(undefined);
    const aditorReference = useRef<any>(null);
    const fullScreenReference = useRef(false);

    const buildDataLocalization =
        revision && revision.buildData && revision.buildData.localizations
            ? revision.buildData.localizations
            : undefined;

    const {
        loading: aditorInitLoading,
        error: aditorInitError,
        data: aditorInitData
    } = useQuery<AditorInitQueryData, AditorSceneVariables>(ADITOR_INIT_QUERY, {
        variables: {
            revisionId: Number(revisionId),
            localizationData: JSON.stringify(buildDataLocalization)
        },
        fetchPolicy: "network-only",
        skip: currentScene.current !== undefined
    });

    const [loadScene] = useLazyQuery<AditorLoadSceneData, AditorSceneVariables>(
        ADITOR_LOAD_SCENE
    );

    const [createSceneMutation] = useMutation<
        CreateAditorSceneData,
        AditorCreateVariables
    >(ADITOR_CREATE_SCENE);

    const [updateSceneMutation] = useMutation<
        UpdateAditorSceneData,
        AditorUpdateVariables
    >(ADITOR_UPDATE_SCENE);

    const [deleteSceneMutation] = useMutation<
        DeleteAditorSceneData,
        AditorSceneVariables
    >(ADITOR_DELETE_SCENE);

    const handleKeydown = useCallback((event: any) => {
        if (fullScreenReference.current && event.key === "Escape") {
            toggleFullscreen(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (aditorReady) {
            const iframe = aditorReference.current;
            if (!iframe) {
                console.log("[DEBUG] iframe not found stopping eventlisteners");
                return;
            }
            const iframeDocument = iframe.contentWindow.document;
            iframeDocument.addEventListener("keydown", (event: any) =>
                handleKeydown(event)
            );
            window.addEventListener("keydown", (event: any) =>
                handleKeydown(event)
            );
            return () => {
                iframeDocument.removeEventListener("keydown", handleKeydown);
                window.removeEventListener("keydown", handleKeydown);
            };
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [aditorReady]);

    useEffect(() => {
        if (
            aditorReady &&
            !aditorInitLoading &&
            !aditorInitError &&
            aditorInitData
        ) {
            updateInitData(aditorInitData);
            sendInitMessageToIframe(aditorInitData);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [aditorInitLoading, aditorInitError, aditorReady]);

    useEffect(() => {
        const fetchHtmlFromS3 = async () => {
            try {
                // Official Aditor
                const response = await fetch(getAditorUrl());
                if (!response.ok) {
                    throw new Error("Failed to fetch the HTML file from S3");
                }

                const htmlData = await response.text();
                setHtmlContent(htmlData);
            } catch (error) {
                console.error("Error fetching the file:", error);
            }
        };
        fetchHtmlFromS3();
    }, []);

    useEffect(() => {
        window.addEventListener("message", handleAditorMessages);
        return () => {
            window.removeEventListener("message", handleAditorMessages);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const sendInitMessageToIframe = (aditorInitData: AditorInitQueryData) => {
        const scopes = ["full"];
        if (
            aditorInitData.aditorLoadScene &&
            aditorInitData.aditorLoadScene.data
        ) {
            getSceneAsBase64(aditorInitData.aditorLoadScene.data)
                .then(result => {
                    const initMessage: AditorMessage = {
                        type: "init",
                        data: {
                            user: {
                                scopes: scopes
                            },
                            revisionName: revision.name || "",
                            projectName: revision.project.name || "",
                            productName: revision.project.product?.name || "",
                            organizationName:
                                revision.project.product?.organization.name ||
                                "",
                            data: result,
                            templates: aditorInitData.aditorTemplates,
                            localizations:
                                revision.buildData.useLegacyLocalizations ===
                                    undefined ||
                                !revision.buildData.useLegacyLocalizations
                                    ? aditorInitData.getLocalizationsJson
                                          ?.localizationJson
                                    : undefined
                        }
                    };
                    sendMessageToIframe(initMessage);
                    if (result) {
                        lastLoadedSceneId.current = Number(
                            aditorInitData.aditorLoadScene.id
                        );
                        currentScene.current =
                            aditorInitData.aditorLoadScene.hash;
                        updateCurrentSceneTag(
                            aditorInitData.aditorLoadScene.tag ||
                                lastLoadedSceneId.current.toString()
                        );
                    }
                })
                .catch(error => {
                    console.error("Error loading scene: ", error);
                });
        } else {
            const initMessage: AditorMessage = {
                type: "init",
                data: {
                    user: {
                        scopes: scopes
                    },
                    revisionName: revision.name || "",
                    projectName: revision.project.name || "",
                    productName: revision.project.product?.name || "",
                    organizationName:
                        revision.project.product?.organization.name || "",
                    data: undefined,
                    templates: aditorInitData.aditorTemplates,
                    localizations:
                        aditorInitData.getLocalizationsJson?.localizationJson
                }
            };
            sendMessageToIframe(initMessage);
        }
    };

    const sendCreatedSceneToIFrame = async (
        sceneData: CreateAditorSceneResult
    ) => {
        try {
            const base64String = await getSceneAsBase64(sceneData.data);
            sendMessageToIframe({
                type: "createdScene",
                data: base64String
            });
        } catch (error) {
            console.log("[DEBUG] sendCreatedSceneToIFrame with error ", error);
        }
    };

    const createScene = (template: string) => {
        const input: AditorCreateInput = {
            revisionId: Number(revisionId),
            template: template
        };
        createSceneMutation({
            variables: {
                input: input
            }
        })
            .then(result => {
                if (result.data) {
                    sendCreatedSceneToIFrame(result.data.aditorCreateScene);
                } else {
                    console.log(
                        "[DEBUG] unexpected result data from createSceneMutation ",
                        result
                    );
                }
            })
            .catch(error =>
                console.log(
                    "[DEBUG] createSceneMutation failed with error ",
                    error
                )
            );
    };

    const deleteScene = (sceneId: number) => {
        deleteSceneMutation({
            variables: {
                revisionId: Number(revisionId),
                sceneId: sceneId
            },
            refetchQueries: [
                {
                    query: ADITOR_GET_SCENES,
                    variables: { revisionId: Number(revisionId) }
                }
            ]
        })
            .then(result => {
                console.log("[DEBUG] delete scene result ", result);
                if (result.data) {
                    if (Number(lastLoadedSceneId.current) === Number(sceneId)) {
                        lastLoadedSceneId.current = undefined;
                        currentScene.current = undefined;
                        updateCurrentSceneTag("");
                        sendMessageToIframe({
                            type: "deletedScene",
                            data: {
                                success: true
                            }
                        });
                    }
                } else {
                    console.log(
                        "[DEBUG] unexpected result data from deleteSceneMutation ",
                        result
                    );
                    sendMessageToIframe({
                        type: "deletedScene",
                        data: {
                            success: false,
                            message:
                                "[DEBUG] unexpected result data from deleteSceneMutation " +
                                result
                        }
                    });
                }
            })
            .catch(error => {
                console.log(
                    "[DEBUG] deleteSceneMutation failed with error ",
                    error
                );
                sendMessageToIframe({
                    type: "deletedScene",
                    data: {
                        success: false,
                        message: error as string
                    }
                });
            });
    };

    const updateScene = async (updateMessage: AditorUpdateData) => {
        const input: AditorUpdateInput = {
            revisionId: Number(revisionId),
            data: updateMessage.data,
            localizations: JSON.stringify(updateMessage.localizations)
        };
        const encoder = new TextEncoder();
        const data = encoder.encode(updateMessage.data);
        const hashBuffer = await crypto.subtle.digest("SHA-256", data);
        currentScene.current = Array.from(new Uint8Array(hashBuffer))
            .map(b => b.toString(16).padStart(2, "0"))
            .join("");

        updateSceneMutation({
            variables: {
                input: input
            },
            refetchQueries: [
                {
                    query: ADITOR_GET_SCENES,
                    variables: { revisionId: Number(revisionId) }
                }
            ]
        })
            .then(result => {
                let success = true;
                let message: string | undefined = undefined;
                if (result.data && result.data.aditorUpdateScene) {
                    console.log(
                        "[DEBUG] result.data.aditorUpdateScene.hash ",
                        result.data.aditorUpdateScene.hash
                    );
                    if (
                        result.data.aditorUpdateScene.hash !==
                        currentScene.current
                    ) {
                        console.log(
                            "[DEBUG] wrong hash received from backend ",
                            result
                        );
                        success = false;
                        message = "wrong hash received from backend";
                    } else {
                        lastLoadedSceneId.current = Number(
                            result.data.aditorUpdateScene.id
                        );
                        updateCurrentSceneTag(
                            result.data.aditorUpdateScene.tag ||
                                lastLoadedSceneId.current.toString()
                        );
                        sendMessageToIframe({
                            type: "updatedScene",
                            data: {
                                success: true
                            }
                        });
                    }
                } else {
                    console.log(
                        "[DEBUG] unexpected result data from updateSceneMutation ",
                        result
                    );
                    success = false;
                    message = "unexpected result data from updateSceneMutation";
                }

                if (
                    result.data &&
                    result.data.updateAllLocalizations !== undefined
                ) {
                    if (
                        result.data.updateAllLocalizations === null ||
                        result.data.updateAllLocalizations.version === null
                    ) {
                        console.log("[DEBUG] no localizations to update");
                    } else {
                        const currentCache = client.readQuery<
                            RevisionData,
                            RevisionVariables
                        >({
                            query: GET_REVISION,
                            variables: { revisionId: Number(revisionId) }
                        });
                        if (currentCache) {
                            const revision = { ...currentCache.revision };
                            if (typeof revision.buildData === "string") {
                                revision.buildData = JSON.parse(
                                    revision.buildData
                                );
                            }

                            const version =
                                result.data?.updateAllLocalizations.version;
                            const partialUpdate = JSON.parse(
                                JSON.stringify(
                                    result.data?.updateAllLocalizations
                                        .partialData
                                )
                            );
                            const updatedBuildData = {
                                ...revision.buildData,
                                version: version,
                                ...partialUpdate
                            };
                            client.writeQuery<RevisionData, RevisionVariables>({
                                query: GET_REVISION,
                                data: {
                                    revision: {
                                        ...revision,
                                        buildData: updatedBuildData
                                    }
                                },
                                variables: { revisionId: Number(revisionId) }
                            });
                        }
                    }
                } else {
                    console.log(
                        "[DEBUG] unexpected result data from updateSceneMutation ",
                        result
                    );
                    success = false;
                    message = "unexpected result data from updateSceneMutation";
                }
                sendMessageToIframe({
                    type: "updatedScene",
                    data: {
                        success: success,
                        message: message
                    }
                });
            })
            .catch(error => {
                console.log(
                    "[DEBUG] updateSceneMutation failed with error ",
                    error
                );
                sendMessageToIframe({
                    type: "updatedScene",
                    data: {
                        success: false,
                        message: error as string
                    }
                });
            });
    };

    const setCurrentScene = (newScene: AditorSceneData) => {
        updateAditorKey(Date.now());
        loadScene({
            variables: {
                revisionId: Number(revisionId),
                sceneId: Number(newScene.id)
            }
        }).then(result => {
            if (result.data && result.data.aditorLoadScene && initData) {
                sendInitMessageToIframe({
                    ...initData,
                    aditorLoadScene: result.data.aditorLoadScene
                });
            }
        });
    };

    const toggleFullscreen = (newState: boolean) => {
        updateFullScreen(newState);
        fullScreenReference.current = newState;
        sendMessageToIframe({
            type: "fullscreen",
            data: newState
        });
    };

    const handleAditorMessages = (event: MessageEvent) => {
        if (event.source !== aditorReference.current?.contentWindow) {
            return;
        }
        // console.log("[DEBUG] handleAditorMessages event: ", event);
        try {
            const messageData = event.data;
            switch (messageData.type) {
                case "aditorReady": {
                    updateAditorReady(true);
                    break;
                }
                case "createScene": {
                    createScene(messageData.data);
                    break;
                }
                case "deleteScene": {
                    if (lastLoadedSceneId.current !== undefined) {
                        deleteScene(lastLoadedSceneId.current);
                    }
                    break;
                }
                case "updateScene": {
                    updateScene(messageData.data);
                    break;
                }
                case "fullscreen": {
                    toggleFullscreen(messageData.data);
                    break;
                }
                case "dirty": {
                    if (typeof messageData.data !== "boolean") {
                        console.log(
                            "[DEBUG] messageData.data contained wrong value type ",
                            messageData.data
                        );
                        break;
                    }
                    setIsBlocking(messageData.data);
                    break;
                }
                default: {
                    console.log(
                        "[DEBUG] message type: ",
                        messageData.type,
                        " not implemented yet"
                    );
                    break;
                }
            }
        } catch (error) {
            console.error(
                "[ERROR] handleAditorMessages failed with error: ",
                error
            );
        }
    };

    const sendMessageToIframe = (message: AditorMessage) => {
        if (aditorReference) {
            // console.log("[DEBUG] sent message ", message);
            aditorReference.current?.contentWindow?.postMessage(message, "*");
        }
    };

    const blocker = useBlocker(
        ({ currentLocation, nextLocation }) =>
            isBlocking && currentLocation.pathname !== nextLocation.pathname
    );

    if (!revision || revision.id < 0) {
        return null;
    }

    return (
        <>
            <div className={fullScreen ? "aditorViewFull" : "aditorViewSmall"}>
                {!fullScreen ? (
                    <div className="p-1 border-bottom border-inverse-25 d-flex">
                        <div className={styles.backButton}>
                            <BackButton
                                hasHistory={
                                    !(location.key && navigationType === "POP")
                                }
                                goBack={() => navigate(-1)}
                                size={"lg"}
                            />
                        </div>
                        <div className="d-flex align-items-center float-end ms-auto pe-3 pt-1">
                            {revision.id !== -1 &&
                            lastLoadedSceneId?.current !== undefined ? (
                                <LatestSceneBadge
                                    revisionId={revision.id}
                                    aditorSceneId={lastLoadedSceneId.current}
                                />
                            ) : null}
                            {currentSceneTag !== "" ? (
                                <InputGroup className="m-0 p-0 me-2 mouseHover">
                                    <InputGroup.Text
                                        className="m-0 p-0 px-2 bg-success text-inverse"
                                        onClick={() =>
                                            updateShowSnapshots({
                                                show: true,
                                                focus: currentSceneTag
                                            })
                                        }
                                    >
                                        {currentSceneTag}
                                    </InputGroup.Text>
                                    <Button
                                        className={cx(
                                            "m-0",
                                            styles.maximizeButton
                                        )}
                                        onClick={() =>
                                            updateShowSnapshots({
                                                show: true,
                                                focus: undefined
                                            })
                                        }
                                        title="Backups"
                                    >
                                        <FontAwesomeIcon
                                            size="1x"
                                            icon={faDatabase}
                                        />
                                    </Button>
                                </InputGroup>
                            ) : (
                                <Button
                                    className={cx(
                                        "m-0 me-2",
                                        styles.maximizeButton
                                    )}
                                    onClick={() =>
                                        updateShowSnapshots({
                                            show: false,
                                            focus: undefined
                                        })
                                    }
                                    title="Backups"
                                >
                                    <FontAwesomeIcon
                                        size="1x"
                                        icon={faDatabase}
                                    />
                                </Button>
                            )}
                            <Button
                                className={styles.maximizeButton}
                                onClick={() => toggleFullscreen(true)}
                                title="Aditor Fullscreen mode"
                            >
                                <FontAwesomeIcon
                                    size="1x"
                                    icon={faUpRightAndDownLeftFromCenter}
                                />
                            </Button>
                        </div>
                    </div>
                ) : null}
                <iframe
                    key={aditorKey}
                    id="aditor-iframe"
                    ref={aditorReference}
                    title="Aditor"
                    srcDoc={htmlContent}
                    width="100%"
                    height="100%"
                    className="border-0"
                />
            </div>
            {showOptimizingModal ? (
                <AditorOptimizingModal
                    revisionId={Number(revisionId)}
                    onClose={() => updateShowOptimizingModal(false)}
                />
            ) : null}
            {showSnapshots ? (
                <AditorSnapshotModal
                    modalConfig={showSnapshots}
                    revisionId={Number(revisionId)}
                    revisionName={revision.name || ""}
                    currentSceneHash={currentScene.current}
                    setCurrentScene={setCurrentScene}
                    updateScene={updateScene}
                    deleteScene={deleteScene}
                    updateCurrentSceneTag={updateCurrentSceneTag}
                    onClose={() =>
                        updateShowSnapshots({
                            show: false,
                            focus: undefined
                        })
                    }
                />
            ) : null}
            {blocker.state === "blocked" ? (
                <AlertModal
                    header={<h5>Are you sure you want to leave?</h5>}
                    component="You have unsaved changes."
                    visible={() => blocker.reset()}
                    footer={
                        <Button
                            variant="warning"
                            className="m-2"
                            onClick={() => blocker.proceed()}
                        >
                            Leave
                        </Button>
                    }
                />
            ) : null}
        </>
    );
};

export const AditorView = React.memo(AditorViewImplementation);
